import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { KeyRound, Loader2, Smartphone } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Public, no-login page behind the "Send login link" text: set a password once, land signed in.
export default function PortalInvite() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { axios.get(`${API}/public/portal/${token}`).then((r) => setD(r.data)).catch((e) => setErr(e?.response?.data?.detail || "This link isn't valid any more.")); }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 6) { setErr("Password needs at least 6 characters"); return; }
    if (pw !== pw2) { setErr("Passwords don't match"); return; }
    setBusy(true); setErr("");
    try {
      const { data } = await axios.post(`${API}/public/portal/${token}`, { password: pw });
      localStorage.setItem("pc_client_token", data.token);
      navigate(`/book/${data.slug}?account=1`, { replace: true });
    } catch (e2) { setErr(e2?.response?.data?.detail || "Something went wrong — ask the clinic for a new link."); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-background flex items-start justify-center p-4 sm:p-8" data-testid="portal-invite-page">
      <div className="w-full max-w-sm space-y-4 pt-6">
        {d?.logo_url ? <img src={d.logo_url} alt="" className="h-12 mx-auto object-contain" /> : <div className="text-center font-heading font-bold text-2xl">{d?.clinic || "Azordi"}</div>}
        {!d && err && <div className="rounded-2xl border bg-card p-6 text-center text-muted-foreground" data-testid="portal-error">{err}</div>}
        {d && (
          <form onSubmit={submit} className="rounded-2xl border bg-card p-5 shadow-sm space-y-4" data-testid="portal-card">
            <div className="flex items-center gap-2 text-primary text-sm font-medium"><KeyRound className="h-4 w-4" />{d.has_account ? `Welcome back${d.first_name ? `, ${d.first_name}` : ""}` : `Hi ${d.first_name || "there"}, let's set up your account`}</div>
            <p className="text-sm text-muted-foreground">{d.has_account ? "Choose a new password to sign in." : `Choose a password for your ${d.clinic} account so you can book, reschedule and see your appointments.`}</p>
            <div className="flex items-center gap-2 text-sm rounded-lg bg-muted px-3 py-2" data-testid="portal-phone"><Smartphone className="h-4 w-4 text-muted-foreground" />Mobile ending {d.phone_masked}</div>
            <Input type="password" placeholder="Password (6+ characters)" value={pw} onChange={(e) => setPw(e.target.value)} className="h-11 rounded-lg" data-testid="portal-password" autoFocus />
            <Input type="password" placeholder="Repeat password" value={pw2} onChange={(e) => setPw2(e.target.value)} className="h-11 rounded-lg" data-testid="portal-password2" />
            {err && <div className="text-sm text-destructive" data-testid="portal-error">{err}</div>}
            <Button type="submit" disabled={busy} className="w-full h-11 rounded-lg" data-testid="portal-submit">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : d.has_account ? "Save & sign in" : "Create my account"}</Button>
            <p className="text-[11px] text-muted-foreground text-center">You'll sign in with your mobile number and this password next time.</p>
          </form>
        )}
      </div>
    </div>
  );
}
